import { useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBackButton } from "@/hooks/useBackButton";
import { Badge } from "@/components/ui/badge";
import { Smartphone, Cpu, MemoryStick, HardDrive, Camera, Battery, Monitor, Star, Wifi, Scale } from "lucide-react";
import { ProductSpec, PhoneRatings, PhoneSpecs } from "@/data/phoneSpecs";

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface PhoneSpecsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ProductSpec | null;
}

const RatingBar = ({ value, label }: { value: number; label: string }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-muted-foreground w-24 text-right">{label}</span>
    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-gold rounded-full transition-all duration-500"
        style={{ width: `${(value / 10) * 100}%` }}
      />
    </div>
    <span className="text-xs font-bold text-foreground w-8">{value.toFixed(1)}</span>
  </div>
);

const SpecRow = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) => (
  <div className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
    <Icon className="w-4 h-4 text-primary flex-shrink-0" />
    <span className="text-sm text-muted-foreground flex-1">{label}</span>
    <span className="text-sm font-semibold text-foreground">{value}</span>
  </div>
);

const defaultRatings: PhoneRatings = { screen: 0, performance: 0, camera: 0, battery: 0, value: 0, overall: 0 };
const defaultSpecs: PhoneSpecs = { screen: '-', processor: '-', ram: '-', storage: '-', camera: '-', frontCamera: '-', battery: '-', os: '-', connectivity: '-', weight: '-' };

const PhoneSpecsModal = ({ open, onOpenChange, product }: PhoneSpecsModalProps) => {
  const closeModal = useCallback(() => onOpenChange(false), [onOpenChange]);
  useBackButton(open, closeModal, "phone-specs");

  if (!product) return null;

  const ratings = { ...defaultRatings, ...(product.ratings || {}) };
  const specs = { ...defaultSpecs, ...(product.specs || {}) };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground">{product.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Image + Price */}
          <div className="flex gap-4 items-start">
            <img
              src={product.image}
              alt={product.name}
              className="w-32 h-32 rounded-xl object-cover border border-border"
            />
            <div className="flex-1">
              <Badge variant="outline" className="mb-2">{product.color}</Badge>
              <p className="text-2xl font-bold text-gradient-gold">{product.price}</p>
              <p className="text-xs text-muted-foreground mt-1">A partir de {formatCurrency(product.priceNum * 0.95)} no Pix</p>
              <div className="flex items-center gap-1 mt-2">
                <Star className="w-4 h-4 text-primary fill-primary" />
                <span className="text-sm font-bold text-foreground">{ratings.overall.toFixed(1)}</span>
                <span className="text-xs text-muted-foreground">/10</span>
              </div>
            </div>
          </div>

          {/* Specs */}
          <div>
            <h4 className="text-sm font-display font-semibold text-foreground mb-3">Especificações</h4>
            <div className="bg-secondary/50 rounded-lg px-4">
              <SpecRow icon={Monitor} label="Tela" value={specs.screen} />
              <SpecRow icon={Cpu} label="Processador" value={specs.processor} />
              <SpecRow icon={MemoryStick} label="Memória RAM" value={specs.ram} />
              <SpecRow icon={HardDrive} label="Armazenamento" value={specs.storage} />
              <SpecRow icon={Camera} label="Câmera Traseira" value={specs.camera} />
              <SpecRow icon={Camera} label="Câmera Frontal" value={specs.frontCamera} />
              <SpecRow icon={Battery} label="Bateria" value={specs.battery} />
              <SpecRow icon={Smartphone} label="Sistema" value={specs.os} />
              <SpecRow icon={Wifi} label="Conectividade" value={specs.connectivity} />
              <SpecRow icon={Scale} label="Peso" value={specs.weight} />
            </div>
          </div>

          {/* Ratings */}
          <div>
            <h4 className="text-sm font-display font-semibold text-foreground mb-3">Avaliações</h4>
            <div className="space-y-2">
              <RatingBar label="Tela" value={ratings.screen} />
              <RatingBar label="Performance" value={ratings.performance} />
              <RatingBar label="Câmera" value={ratings.camera} />
              <RatingBar label="Bateria" value={ratings.battery} />
              <RatingBar label="Custo-benefício" value={ratings.value} />
              <RatingBar label="Geral" value={ratings.overall} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneSpecsModal;
